import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import QuizOrSurveyModal from "../../../../components/CourseBuilder/QuizOrSurveyModal";
import { useDispatch, useSelector } from "react-redux";
import {
  createSurvey,
  updateSurvey,
  createQuestions,
  updateQuestion,
  fetchSurveys,
  updateQuestions,
  updateSurveyQuestions,
  importSurveyQuestions
} from "../../../../redux/actions/contentSurveyActions";
import { useItems } from "../../../../hooks/courseBuilder";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import LoadingSpinnerWithBackdrop from "../../../../components/LoadingSpinner/LoadingSpinnerWithBackdrop";
import QuestionBankModal from "../../../../components/QuestionBank/QuestionBankModal";
import { surveyQuestionTypes } from "../../../../constants";
import EditQuestion from "./EditQuestion";
import { fetchCourseSections } from "../../../../redux/actions/contentCoursesActions";
import { courseTypes } from "../../../../constants/index";
import AddRepoItemVersionConfirmationModal from "../../../../components/WarningModal/AddRepoItemVersionConfirmationModal";
import { getCourseNames } from "../../../../utils/course_utils";

const selector = makeGetEntities();

const CourseSurveyContainer = ({
  onClose,
  open,
  existingSurveyId,
  versionId,
  shortCourseId
}) => {
  const dispatch = useDispatch();
  const [questionBankOpen, setQuestionBankOpen] = useState(false);
  const [editQuestionModalOpen, setEditQuestionModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [newQuestionType, setNewQuestionType] = useState(
    surveyQuestionTypes.trueFalse
  );

  const [surveyId, setSurveyId] = useState("");
  const [surveyName, setSurveyName] = useState("");
  const [updatedQuestionsData, setUpdatedQuestionsData] = useState(null);
  const [quizFormErrors, setQuizFormErrors] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [associatedCourses, setAssociatedCourses] = useState([]);
  const [newSurveyId, setNewSurveyId] = useState(null);

  const { deleteItem } = useItems();

  const { survey, loading, trainingObject, itemId } = useSelector(
    ({ contentSurveysState, contentCoursesState }) => ({
      survey: surveyId
        ? selector(contentSurveysState, {
            type: "surveys",
            id: surveyId
          })
        : null,
      trainingObject: selector(contentCoursesState, {
        type: "version",
        id: versionId
      }),
      itemId:
        surveyId && contentSurveysState.item
          ? Object.keys(contentSurveysState.item)[0]
          : null,
      loading: contentSurveysState.loading
    })
  );

  useEffect(() => {
    /**
     * if Survey ID is not null, it means the survey is being modified and already exists,
     * so we need to fetch the existing survey data
     **/
    if (existingSurveyId) {
      dispatch(
        fetchSurveys(existingSurveyId, { include: "questions.answerChoices" })
      );
    }
    setSurveyId(existingSurveyId);
  }, [existingSurveyId]);

  const handleCreate = newSurveyData => {
    dispatch(createSurvey(newSurveyData)).then(response => {
      if (response.errors) {
        setQuestionBankOpen(false);
        setQuizFormErrors(response.errors);
        return;
      } else {
        /**
         * When the quiz is successfully created, we fetch the course sections so
         * the course sections will be automatically updated in the course builder
         **/
        dispatch(fetchCourseSections(versionId));
        setNewSurveyId(response.data.id);
        setQuestionBankOpen(true);
        setSurveyId(response.data.id);
        setSurveyName(response.data.attributes.name);
        return;
      }
    });
  };

  const updateSurveyData = (newSurveyData, updateCurrentSurveyAttributes) => {
    return dispatch(
      updateSurvey(surveyId, newSurveyData, updateCurrentSurveyAttributes)
    ).then(response => {
      if (response.errors) {
        setQuestionBankOpen(false);
        setQuizFormErrors(response.errors);
      } else {
        dispatch(fetchCourseSections(versionId));
        setNewSurveyId(response.data.id);
        if (!updateCurrentSurveyAttributes) setSurveyId(response.data.id);
        setSurveyName(response.data.attributes.name);
        setQuestionBankOpen(true);
      }
      setConfirmationModalOpen(false);
      setUpdatedQuestionsData(null);
    });
  };

  const handleUpdate = newSurveyData => {
    if (newSurveyId) {
      updateSurveyData(newSurveyData, true);
    } else {
      if (!!survey && survey.associatedCourses) {
        setAssociatedCourses(getCourseNames(survey.associatedCourses));
      }
      updateSurveyData(newSurveyData);
    }
  };

  const handleSaveQuestions = newQuestionsData => {
    if (
      !confirmationModalOpen &&
      trainingObject &&
      trainingObject.type === courseTypes.ilt &&
      associatedCourses.length > 0
    ) {
      setUpdatedQuestionsData(newQuestionsData);
      setConfirmationModalOpen(true);
      return;
    }
    const surveyQuestionsRelationships = newQuestionsData.map(
      ({ id, type }) => ({
        id,
        type
      })
    );
    dispatch(updateQuestions(newQuestionsData));
    dispatch(updateSurveyQuestions(surveyId, surveyQuestionsRelationships));
    setQuestionBankOpen(false);
    setSurveyId("");
    onClose();
  };

  const handleAddNewQuestion = questionType => {
    setNewQuestionType(questionType);
    setEditQuestionModalOpen(true);
  };

  const handleEditQuestion = question => {
    setNewQuestionType(question.questionType);
    setSelectedQuestion(question);
    setEditQuestionModalOpen(true);
  };

  const handleSaveQuestion = (
    newQuestionData,
    newQuestionTypeToCreate,
    questionId,
    resetForm
  ) => {
    if (questionId) {
      dispatch(updateQuestion(newQuestionData, questionId)).then(response => {
        setUpdates(newQuestionTypeToCreate, resetForm);
      });
    } else {
      dispatch(createQuestions(newQuestionData)).then(response => {
        setUpdates(newQuestionTypeToCreate, resetForm);
      });
    }
  };

  const handleQuizOrSurveyClose = () => {
    if (
      surveyId &&
      itemId &&
      existingSurveyId !== undefined &&
      surveyId !== existingSurveyId
    ) {
      deleteItem({ itemId: itemId }).then(response =>
        dispatch(fetchCourseSections(versionId))
      );
    }
    setSurveyId("");
    setSurveyName("");
    setNewSurveyId(null);
    onClose();
  };

  const setUpdates = (newQuestionTypeToCreate, resetForm) => {
    if (!newQuestionTypeToCreate) {
      setEditQuestionModalOpen(false);
    } else {
      resetForm();
      setNewQuestionType(newQuestionTypeToCreate);
    }
    setSelectedQuestion(null);
  };

  const handleUploadQuestions = async file => {
    const response = await dispatch(importSurveyQuestions(file, surveyId));
    if (response.errors) {
      dispatch(fetchSurveys(surveyId, { include: "questions.answerChoices" }));
    }
    return response;
  };

  return (
    <>
      {loading && <LoadingSpinnerWithBackdrop />}
      <QuizOrSurveyModal
        onClose={handleQuizOrSurveyClose}
        open={open}
        onSubmit={existingSurveyId || newSurveyId ? handleUpdate : handleCreate}
        type="survey"
        quizOrSurveyData={survey}
        versionId={versionId}
        shortCourseId={shortCourseId}
        loading={loading}
        isEditing={existingSurveyId ? true : false}
        errors={quizFormErrors}
      />
      <QuestionBankModal
        open={questionBankOpen}
        onClose={() => {
          setQuestionBankOpen(false);
          setSelectedQuestion(null);
        }}
        title={surveyName}
        questions={survey ? survey.questions : []}
        type="survey"
        surveyId={surveyId}
        onSubmit={handleSaveQuestions}
        onAddNewQuestion={handleAddNewQuestion}
        onEditQuestion={handleEditQuestion}
        onUploadQuestions={handleUploadQuestions}
      />
      <EditQuestion
        open={editQuestionModalOpen}
        onClose={() => {
          setEditQuestionModalOpen(false);
          setSelectedQuestion(null);
        }}
        newQuestionType={newQuestionType}
        isSurveyQuestion={true}
        surveyId={surveyId}
        onSaveQuestion={handleSaveQuestion}
        existingQuestionData={selectedQuestion}
      />
      <AddRepoItemVersionConfirmationModal
        courseNames={associatedCourses}
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        onSubmit={() => handleSaveQuestions(updatedQuestionsData)}
      />
    </>
  );
};

CourseSurveyContainer.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create the quiz/survey
   */
  onSubmit: PropTypes.func,
  /**
   * if a Survey ID is passed in, it means the user is modifying an existing survey, ortherwise if it's null
   * that means a new survey is being created
   */
  existingSurveyId: PropTypes.string,
  /**
   * ID of the child course being edited
   */
  versionId: PropTypes.string,
  /**
   * ID of the course section quiz is being added to
   */
  shortCourseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default CourseSurveyContainer;
